import {
    exam_getExaminationPaperById_Api,
    exam_setAsAnswer_Api,
    exam_submitExaminationPaper_Api,
    examinationPaperSchema,
    examinationSchema,
} from 'apis'
import {getId} from 'apis/model/base'
import {setLocalExaminationPaperKey} from 'helpers/localStorage'
import _ from 'lodash'
import useAsync from 'modules/asyncCache/useAsync'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import {useHistory} from 'modules/navigation/useRouter'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {useSelectEntities} from 'redux/entities/useSelectEntities'
import {getResponseItem, NullAsDefault} from 'views/Shared'
import {notifyOnError} from '../factory/createErrorEvent'
import {bindQueryParam} from '../functions/routerHelper'
import ExamCountdown from './components/ExamCountdown'
import {getLinkToExamination} from './ExaminationContext'
import {getRouteSearchParams, useRouteSearchParam} from "../../../envs/_current/history";

export const ExamActions = Object.freeze({
  CLEAR: 'clear',
  RESULT: 'result',
  TIMEOUT: 'timeout',
  REVIEW: 'review',
  EXAMINATION_PAGE: 'examination-page',
})

export const ExamStatus = Object.freeze({
  WAITING: 'waiting',
  STARTED: 'started',
  STOPPED: 'stopped',
})

export const examFilterActions = {
  review: 'review',
  question: 'question',
}

export const QuestionTypes = Object.freeze({
  TEXT: {
    label: 'texts',
    value: 'text',
  },
  SINGLE: {
    label: 'single',
    value: 'single',
  },
  MULTIPLE: {
    label: 'multiple',
    value: 'multiple',
  },
  BOOL: {
    label: 'bool',
    value: 'bool',
  },
})

const ExaminationPaperContext = React.createContext({})

export function examSortBy(
  array = [],
  fieldName = 'order_sort',
  defaultValue = 0
) {
  if (!_.isArray(array) || _.isEmpty(array)) {
    return []
  }
  const result = Array.from(array)
  return result.sort((a, b) =>
    _.defaultTo(_.get(a, fieldName), defaultValue) >
    _.defaultTo(_.get(b, fieldName), defaultValue)
      ? 1
      : -1
  )
}

export const ExaminationPaperProvider = ({id: paper_id, children}) => {
  const t = useTranslate()

  const history = useHistory()

  const location = useLocation()

  const [visited_questions, set_visited_questions] = useState([])

  const [questionIndex, setQuestionIndex] = useState(0)

  const [navigation, setNavigation] = useState({
    canBack: false,
    canNext: false,
  })

  const [countdownToken, setCountdownToken] = useState()

  const [status, setStatus] = useState(ExamStatus.WAITING)

  const [isSubmitting, setSubmitting] = useState(false)

  const [answers, setAnswers] = useState({})

  const [action, setAction] = useState({
    type: undefined,
    value: undefined,
  })

  const {isLoading = false, response} = useAsync({
    query: bindQueryParam({id: paper_id}),
    apiInfo: exam_getExaminationPaperById_Api,
  })

  useEffect(() => {
    if (paper_id) {
      setLocalExaminationPaperKey(paper_id)
    }
  }, [paper_id])

  const paper = useSelectEntities(
    paper_id,
    examinationPaperSchema,
    getResponseItem(response)
  )

  if (paper) {
    setLocalExaminationPaperKey(paper)
  }

  const {examination, examination_paper_questions} = paper || {}

  const examination_id = getId(examination)

  const item = useSelectEntities(
    getId(examination),
    examinationSchema,
    examination
  )

  const questions = examSortBy(examination_paper_questions)

  const markAsRead = useCallback(
    (value) => {
      let current = _.find(visited_questions, {id: value})
      if (current) {
        current.marked = true
        set_visited_questions([
          ...visited_questions.filter(({id}) => id !== value),
          current,
        ])
      }
    },
    [visited_questions]
  )

  useEffect(() => {
    const _array = paper?.examination_paper_questions || []
    set_visited_questions(
      _array.map(({id}) => ({
        id,
        marked: false,
      }))
    )
  }, [paper.examination_paper_questions])

  const renderCountdown = useCallback(
    (params) => (
      <ExamCountdown
        key={countdownToken}
        onInit={() => {
          if (paper?.status !== 1) {
            setStatus(ExamStatus.STARTED)
          }
        }}
        {...{
          trigger: status === ExamStatus.STOPPED,
          autoStart: status === ExamStatus.STARTED,
        }}
        {...(params || {})}
      />
    ),
    [status, paper.status, countdownToken]
  )

  const resetCountdown = () => {
    setCountdownToken(Date.now())
  }

  const onCancel = () => {
    setAction({})
  }

  const [token, setToken] = useState()

  const onClear = () => {
    setAnswers({})
    resetCountdown()
    setToken(Date.now())
    setSubmitting(false)
    setStatus(ExamStatus.WAITING)
    onCancel()
  }

  const redirect = () => {
    onClear()
    history.push(getLinkToExamination(examination_id))
  }

  const onStop = () => {
    setStatus(ExamStatus.STOPPED)
  }

  const handleSubmit = () => {
    setSubmitting(true)
    onStop()
    submit()
  }

  const handleAction = (type, params) => {
    switch (type) {
      case ExamActions.REVIEW:
        setAction({type})
        break
      case ExamActions.EXAMINATION_PAGE:
        redirect()
        break
      case ExamActions.CLEAR:
        onClear()
        break
      case ExamActions.RESULT:
        setAction({type, value: params})
        break
      case ExamActions.TIMEOUT:
        onStop()
        setAction({
          type: ExamActions.RESULT,
          value: params,
        })
        break
      default:
        break
    }
  }

  const {handleAsyncAction: submit = NullAsDefault} = useAsyncAction({
    query: bindQueryParam({id: paper_id}),
    apiInfo: exam_submitExaminationPaper_Api,
    onError: (e, o) => {
      setSubmitting(false)
      notifyOnError(t)(e, o)
    },
    onSuccess: () => {
      setSubmitting(false)
      handleAction(ExamActions.RESULT, {expired: false})
    },
  })

  useEffect(() => {
    if (_.get(paper, 'status', -1) === 1) {
      handleAction(ExamActions.TIMEOUT, {expired: true})
    }
  }, [paper])

  const review_mode = useRouteSearchParam(examFilterActions.review)

  const questionNumber = useRouteSearchParam(examFilterActions.question)

  const navigate = (index) => {
    let searchParams = getRouteSearchParams()
    searchParams.set(examFilterActions.question, _.defaultTo(index, 0))
    history.replace({
      ...location,
      search: '?' + searchParams.toString(),
    })
  }

  const question = useMemo(() => {
    if (paper.status === 1 && !!!review_mode) {
      return null
    }
    if (review_mode && _.isEmpty(questionNumber)) {
      return null
    }
    const index = Number(questionIndex)
    if (index < questions.length) {
      return questions[index]
    } else {
      return null
    }
  }, [questions, questionIndex, review_mode, questionNumber, paper.status])

  useEffect(() => {
    let timeout
    if (question) {
      timeout = setTimeout(() => {
        navigate(questionIndex)
      }, 0)
      markAsRead(getId(question))
      return () => clearTimeout(timeout)
    }
  }, [question, navigation, questionIndex])

  const allRead = useMemo(
    () => !!!_.find(visited_questions, ({marked}) => !!!marked),
    [visited_questions]
  )

  const allDone = useMemo(
    () =>
      _.every(
        examination_paper_questions,
        ({status}) => Number(status) === 1
      ),
    [paper.examination_paper_questions]
  )

  useEffect(() => {
    const new_index = _.defaultTo(Number(questionNumber), 0)
    setQuestionIndex(new_index)
  }, [questionNumber])

  const forward = () => {
    const new_index = Number(questionIndex + 1)
    if (new_index < questions.length) {
      navigate(new_index)
    }
  }

  const backward = () => {
    if (questionIndex > 0 && questionIndex < questions.length) {
      navigate(questionIndex - 1)
    }
  }

  const {handleAsyncAction: commitAnswer = NullAsDefault} = useAsyncAction({
    apiInfo: exam_setAsAnswer_Api,
    onError: notifyOnError(t),
    onSuccess: () => {
      setSubmitting(false)
    },
  })

  const onResolve = (question_id, params = {}) => {
    setSubmitting(true)
    setAnswers({
      ...answers,
      [question_id]: {
        params,
        id: question_id,
      },
    })
    commitAnswer(params, {id: question_id})
  }

  useEffect(() => {
    let params = {
      canBack: false,
      canNext: false,
    }
    if (questions.length > 0) {
      params.canBack = questionIndex > 0
      params.canNext = questionIndex < Math.max(0, questions.length - 1)
    }
    setNavigation(params)
  }, [
    token,
    questionIndex,
    questions.length,
    paper.examination_paper_questions,
  ])

  return (
    <ExaminationPaperContext.Provider
      key={[token, paper_id, examination_id]}
      value={{
        item,
        paper,
        action,
        answers,
        question,
        questions,
        navigation,
        isSubmitting,
        isLoading,
        review_mode,
        examFilterActions,
        status,
        allRead,
        allDone,
        setStatus,
        redirect,
        navigate,
        forward,
        backward,
        onResolve,
        handleAction,
        handleSubmit,
        setSubmitting,
        renderCountdown,
        resetCountdown,
      }}>
      {children}
    </ExaminationPaperContext.Provider>
  )
}

export default ExaminationPaperContext
